import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, Plus, Trash, FloppyDisk, PencilSimple, ClipboardText, Folders, CheckCircle, FilePdf, FileDoc } from "@phosphor-icons/react";

const FIELD_TYPES = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "date", label: "Date" },
  { value: "datetime", label: "Date & time" },
  { value: "select", label: "Dropdown (single)" },
  { value: "multiselect", label: "Checklist (multi)" },
  { value: "checkbox", label: "Checkbox (yes/no)" },
  { value: "signature", label: "Signature" },
  { value: "info", label: "Section / instructions (display only)" },
];

function FieldInput({ field, value, onChange }) {
  const t = field.field_type;
  if (t === "textarea") return <Textarea data-testid={`fill-${field.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-none mt-1 font-mono text-sm" />;
  if (t === "date") return <Input data-testid={`fill-${field.id}`} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-none mt-1" />;
  if (t === "datetime") return <Input data-testid={`fill-${field.id}`} type="datetime-local" value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-none mt-1" />;
  if (t === "signature") return <Input data-testid={`fill-${field.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Type full legal name to sign" className="rounded-none mt-1 italic font-serif" style={{ fontFamily: "'Brush Script MT', cursive" }} />;
  if (t === "checkbox") return (
    <label className="flex items-center gap-2 mt-2 text-sm"><Checkbox data-testid={`fill-${field.id}`} checked={!!value} onCheckedChange={(c) => onChange(!!c)} className="rounded-none" /> Yes</label>
  );
  if (t === "select") return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={`fill-${field.id}`} className="rounded-none mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
      <SelectContent className="max-h-72">{(field.options || []).map((o, i) => <SelectItem key={`${o}-${i}`} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
  if (t === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
    return (
      <div className="mt-1 space-y-1" data-testid={`fill-${field.id}`}>
        {(field.options || []).map((o, i) => (
          <label key={`${o}-${i}`} className="flex items-center gap-2 text-sm"><Checkbox checked={arr.includes(o)} onCheckedChange={() => toggle(o)} className="rounded-none" />{o}</label>
        ))}
      </div>
    );
  }
  return <Input data-testid={`fill-${field.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-none mt-1" />;
}

function FillDialog({ form, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    for (const f of form.fields) {
      if (f.field_type === "info") continue;
      if (f.required) {
        const v = values[f.id];
        const empty = v === undefined || v === "" || (Array.isArray(v) && v.length === 0);
        if (empty) return toast.error(`"${f.label}" is required`);
      }
    }
    setSaving(true);
    try {
      await api.post(`/forms/${form.id}/submissions`, { values, notes });
      toast.success("Submission saved");
      setOpen(false); setValues({}); setNotes("");
      onSubmitted();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="fill-form-btn" className="rounded-none bg-primary hover:bg-primary/90"><ClipboardText size={16} className="mr-1" />Fill Out</Button></DialogTrigger>
      <DialogContent className="rounded-none max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{form.title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {form.fields.map((f) => (
            f.field_type === "info" ? (
              <div key={f.id} className="border-l-4 border-black bg-secondary/40 px-3 py-2" data-testid={`info-${f.id}`}>
                <div className="font-semibold text-sm uppercase tracking-wide">{f.label}</div>
                {f.help && <div className="text-xs text-muted-foreground whitespace-pre-line mt-1">{f.help}</div>}
              </div>
            ) : (
              <div key={f.id}>
                <Label>{f.label}{f.required && <span className="text-red-600 ml-1">*</span>}</Label>
                {f.help && <div className="text-xs text-muted-foreground">{f.help}</div>}
                <FieldInput field={f} value={values[f.id]} onChange={(v) => setValues({ ...values, [f.id]: v })} />
              </div>
            )
          ))}
          <div><Label>Notes <span className="text-muted-foreground">(optional)</span></Label>
            <Textarea data-testid="fill-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-none mt-1 font-mono text-sm" /></div>
          <Button data-testid="submit-fill-btn" onClick={submit} disabled={saving} className="rounded-none w-full bg-primary hover:bg-primary/90">
            <FloppyDisk size={16} className="mr-1" />{saving ? "Saving…" : "Submit"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function FormBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const { canEdit, isRole } = useAuth();
  const [form, setForm] = useState(null);
  const [subs, setSubs] = useState([]);
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => {
    api.get(`/forms/${id}`).then((r) => setForm(r.data)).catch(() => toast.error("Form not found"));
  }, [id]);
  const loadSubs = useCallback(() => { api.get(`/forms/${id}/submissions`).then((r) => setSubs(r.data)).catch(() => {}); }, [id]);
  useEffect(() => { load(); loadSubs(); }, [load, loadSubs]);

  if (!form) return <div className="p-8 font-mono text-sm">Loading…</div>;

  const setField = (i, key, val) => {
    const fields = [...form.fields];
    fields[i] = { ...fields[i], [key]: val };
    setForm({ ...form, fields });
  };
  const setOptions = (i, text) => setField(i, "options", text.split("\n").map((s) => s.trim()).filter(Boolean));
  const addField = () => setForm({ ...form, fields: [...form.fields, { id: crypto.randomUUID(), label: "New field", field_type: "text", required: false, help: "", options: [] }] });
  const removeField = (i) => setForm({ ...form, fields: form.fields.filter((_, idx) => idx !== i) });

  const save = async () => {
    try {
      const { data } = await api.put(`/forms/${id}`, {
        title: form.title, description: form.description, purpose: form.purpose,
        form_type: form.form_type, linked_requirements: form.linked_requirements || [],
        linked_controls: form.linked_controls || [], fields: form.fields,
      });
      setForm({ ...data, submission_count: form.submission_count });
      setEditing(false);
      toast.success("Form saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteSub = async (sid) => {
    try { await api.delete(`/forms/${id}/submissions/${sid}`); loadSubs(); toast.success("Submission deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const promoteSub = async (sid) => {
    try {
      const { data } = await api.post(`/forms/${id}/submissions/${sid}/promote-to-evidence`);
      loadSubs();
      toast.success(`Added to Evidence: ${data.name}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportSub = async (sid, fmt) => {
    try {
      const res = await api.get(`/forms/${id}/submissions/${sid}/export?format=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      const safe = (form.title || "form").replace(/[^A-Za-z0-9]+/g, "_");
      a.href = url; a.download = `${safe}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  const exportBlank = async (fmt) => {
    try {
      const res = await api.get(`/forms/${id}/export-blank?format=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      const safe = (form.title || "form").replace(/[^A-Za-z0-9]+/g, "_");
      a.href = url; a.download = `${safe}_blank.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(fmt === "docx" ? "Blank Word form downloaded (checkboxes are clickable in Word)" : "Blank PDF downloaded");
    } catch (e) { toast.error("Download failed"); }
  };

  const labelFor = (fid) => form.fields.find((f) => f.id === fid)?.label || fid;
  const renderVal = (v) => Array.isArray(v) ? v.join(", ") : (v === true ? "Yes" : v === false ? "No" : String(v ?? ""));

  return (
    <div>
      <div className="border-b border-border px-8 py-5 bg-card sticky top-0 z-10 flex items-start justify-between">
        <div>
          <button onClick={() => nav("/sop-forms")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"><ArrowLeft size={14} /> SOP & Forms</button>
          <h1 className="font-display font-black text-2xl" data-testid="form-builder-title">{form.title}</h1>
          <div className="flex items-center gap-2 mt-1">
            {form.form_type === "cxc" && <span className="border border-amber-400 bg-amber-50 text-amber-700 px-1.5 py-0.5 text-[11px] font-semibold uppercase">CXC</span>}
            <span className="font-mono text-xs text-muted-foreground">{form.fields.length} fields</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="blank-docx-btn" variant="outline" onClick={() => exportBlank("docx")} className="rounded-none"><FileDoc size={16} className="mr-1" />Blank Word</Button>
          <Button data-testid="blank-pdf-btn" variant="outline" onClick={() => exportBlank("pdf")} className="rounded-none"><FilePdf size={16} className="mr-1" />Blank PDF</Button>
          <FillDialog form={form} onSubmitted={loadSubs} />
          {canEdit && !editing && <Button data-testid="edit-form-btn" variant="outline" onClick={() => setEditing(true)} className="rounded-none"><PencilSimple size={16} className="mr-1" />Edit Fields</Button>}
          {canEdit && editing && <Button data-testid="save-fields-btn" onClick={save} className="rounded-none bg-primary hover:bg-primary/90"><FloppyDisk size={16} className="mr-1" />Save</Button>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-8">
        <div className="lg:col-span-2 space-y-4">
          {editing && (
            <div className="border border-border bg-card p-4 space-y-3">
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>Description</Label><Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none mt-1 font-mono text-sm" /></div>
            </div>
          )}
          {form.fields.map((f, i) => (
            <div key={f.id} className="border border-border bg-card" data-testid={`field-${i}`}>
              {editing ? (
                <div className="p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Input value={f.label} onChange={(e) => setField(i, "label", e.target.value)} className="rounded-none font-semibold" />
                    <button onClick={() => removeField(i)} className="text-muted-foreground hover:text-red-600"><Trash size={18} /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Type</Label>
                      <Select value={f.field_type} onValueChange={(v) => setField(i, "field_type", v)}>
                        <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <label className="flex items-end gap-2 text-sm pb-2"><Checkbox checked={f.required} onCheckedChange={(c) => setField(i, "required", !!c)} className="rounded-none" /> Required</label>
                  </div>
                  <div><Label className="text-xs">Help text</Label><Input value={f.help || ""} onChange={(e) => setField(i, "help", e.target.value)} className="rounded-none mt-1" /></div>
                  {(f.field_type === "select" || f.field_type === "multiselect") && (
                    <div><Label className="text-xs">Options (one per line)</Label>
                      <Textarea value={(f.options || []).join("\n")} onChange={(e) => setOptions(i, e.target.value)} className="rounded-none mt-1 font-mono text-sm" /></div>
                  )}
                </div>
              ) : (
                <div className="px-4 py-3">
                  <div className="flex items-center gap-2"><span className="font-semibold text-sm">{f.label}</span>
                    {f.required && <span className="text-red-600 text-xs">*</span>}
                    <span className="ml-auto font-mono text-xs text-muted-foreground">{f.field_type}</span></div>
                  {f.help && <div className="text-xs text-muted-foreground mt-0.5">{f.help}</div>}
                  {f.options?.length > 0 && <div className="text-xs text-muted-foreground mt-1">{f.options.join(" · ")}</div>}
                </div>
              )}
            </div>
          ))}
          {editing && <Button onClick={addField} variant="outline" className="rounded-none"><Plus size={16} className="mr-1" />Add Field</Button>}
        </div>

        <div>
          <div className="border border-border bg-card">
            <div className="border-b border-border px-4 py-3 font-display font-bold">Submissions ({subs.length})</div>
            <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
              {subs.length === 0 ? <div className="p-4 text-sm text-muted-foreground">No submissions yet.</div> : subs.map((s) => (
                <div key={s.id} className="p-4 text-sm" data-testid={`submission-${s.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-muted-foreground">{s.submitted_by} · {s.submitted_at?.slice(0, 16).replace("T", " ")}</span>
                    {canEdit && <button onClick={() => deleteSub(s.id)} className="text-muted-foreground hover:text-red-600"><Trash size={14} /></button>}
                  </div>
                  <div className="mt-2 space-y-1">
                    {Object.entries(s.values || {}).map(([k, v]) => (
                      <div key={k}><span className="font-semibold">{labelFor(k)}:</span> <span className="text-muted-foreground">{renderVal(v)}</span></div>
                    ))}
                    {s.notes && <div className="text-xs text-muted-foreground italic mt-1">Note: {s.notes}</div>}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <button data-testid={`export-pdf-${s.id}`} onClick={() => exportSub(s.id, "pdf")}
                      className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground">
                      <FilePdf size={14} /> PDF
                    </button>
                    <button data-testid={`export-docx-${s.id}`} onClick={() => exportSub(s.id, "docx")}
                      className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground">
                      <FileDoc size={14} /> Word
                    </button>
                    {s.evidence_id ? (
                      <button data-testid={`view-evidence-${s.id}`} onClick={() => nav("/evidence")}
                        className="flex items-center gap-1 text-xs font-semibold text-green-700 hover:underline">
                        <CheckCircle size={14} weight="fill" /> In Evidence — view
                      </button>
                    ) : (canEdit || isRole("reviewer")) && (
                      <button data-testid={`promote-evidence-${s.id}`} onClick={() => promoteSub(s.id)}
                        className="flex items-center gap-1 border border-black px-2 py-1 text-xs font-semibold hover:bg-primary hover:text-white">
                        <Folders size={12} /> Promote to Evidence
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
